import React, { useCallback, useEffect, useState } from 'react';
import { Building2, Globe, Mail } from 'lucide-react';
import organizationsService from '../services/organizationsService';

/**
 * The receiving end of an invitation — "what can I join right now".
 *
 * This existed everywhere except here. The server had the endpoints, the
 * service had the three calls, and `OrganizationsList` already rendered a Join
 * button behind an `onJoin` prop that nothing ever passed. So the admin side
 * was complete and honest ("Invitation sent to X. They can accept it when they
 * sign up") while X had no surface anywhere in the app on which to accept.
 * Found in review; this closes it.
 *
 * Two kinds of offer, deliberately shown in one list rather than two panels:
 *
 *   * an INVITATION — somebody named this address. It carries the role they
 *     chose, which is worth showing: joining as an admin and joining as a
 *     member are different things to agree to.
 *   * a DOMAIN match — nobody named them; their verified address happens to
 *     end in a domain an organisation has verified. It grants "member" only.
 *
 * Both are OFFERS. Neither joins anybody on load — that decision is the whole
 * reason this is a screen and not a side effect of signing in. Somebody whose
 * work address matches a company they have left, or contract for, must be able
 * to look at the offer and close the dialog.
 */
export default function OrganizationJoin({ onClose, onJoined }) {
  const [offers, setOffers] = useState(null);
  const [busyKey, setBusyKey] = useState(null);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setOffers(await organizationsService.getJoinable());
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const rows = await organizationsService.getJoinable();
        if (!cancelled) setOffers(rows);
      } catch {
        // An empty list rather than an error: "nothing to join" is the true
        // answer for almost everyone who opens this, and a failure banner
        // would read as though something were wrong with their account.
        if (!cancelled) setOffers([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const keyOf = (o) => `${o.kind}:${o.organization.id}`;

  const take = async (offer) => {
    setBusyKey(keyOf(offer));
    setError('');
    try {
      if (offer.kind === 'invitation') {
        await organizationsService.acceptInvitation(offer.token);
      } else {
        await organizationsService.joinByDomain(offer.organization.id);
      }
      await onJoined();
      onClose();
    } catch (e) {
      const status = e?.response?.status;
      // Each of these is a state the offer list can genuinely be stale in —
      // the list was fetched when the dialog opened and the answer is decided
      // when the button is pressed. Re-fetching afterwards is what makes the
      // message true rather than just apologetic.
      if (status === 409) {
        setError('You are already a member of that organization.');
      } else if (status === 410) {
        setError('That invitation has expired. Ask them to send a new one.');
      } else if (status === 403) {
        setError('That offer is no longer available to this account.');
      } else {
        setError('Could not join. Try again in a moment.');
      }
      try {
        await load();
      } catch {
        // Keep the list as it is; the message above already says what happened.
      }
    } finally {
      setBusyKey(null);
    }
  };

  return (
    <div className="org-modal-backdrop" onClick={onClose}>
      <div className="org-modal" onClick={(e) => e.stopPropagation()}>
        <h3 className="org-modal-title">Join an organization</h3>

        {offers === null && <p className="org-hint">Checking…</p>}

        {offers?.length === 0 && (
          <p className="org-hint">
            Nothing to join right now. Invitations appear here once somebody sends one to
            your email address.
          </p>
        )}

        {offers?.map((o) => {
          const invitation = o.kind === 'invitation';
          return (
            <div key={keyOf(o)} className="orgx-offer">
              <span className="orgx-offer-icon" aria-hidden>
                <Building2 size={16} />
              </span>
              <div className="orgx-offer-texts">
                <span className="orgx-offer-name">{o.organization.name}</span>
                <span className="orgx-offer-meta">
                  {invitation ? (
                    <>
                      <Mail size={12} aria-hidden /> Invited as {o.role}
                    </>
                  ) : (
                    <>
                      {/* Said plainly, because "we already know where you work"
                          is unsettling when it arrives unexplained. */}
                      <Globe size={12} aria-hidden /> Your email domain is verified by them
                    </>
                  )}
                </span>
              </div>
              <button
                type="button"
                className="org-btn org-btn--primary"
                disabled={busyKey === keyOf(o)}
                onClick={() => take(o)}
              >
                {invitation ? 'Accept' : 'Join'}
              </button>
            </div>
          );
        })}

        {error && <p className="org-error">{error}</p>}

        <div className="org-modal-actions">
          <button type="button" className="org-btn" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
